import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';
import type { FieldValues, UseFormSubscribe } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';

export type UseOnFormValuesChangeOptions<TFormValues extends FieldValues> = {
    allowInvalid?: boolean;
    debounceDelay?: number;
    subscribe: UseFormSubscribe<TFormValues>;
    onChange: (data: TFormValues) => void;
};

export function useOnFormValuesChange<TFormValues extends FieldValues>({
    allowInvalid = false,
    debounceDelay = 1000,
    subscribe,
    onChange,
}: UseOnFormValuesChangeOptions<TFormValues>): void {
    const lastValues = useRef<TFormValues | undefined>(undefined);

    const deduplicateOnChange = useCallback(
        (data: TFormValues) => {
            if (!isEqual(lastValues.current, data)) {
                if (lastValues.current !== undefined) {
                    onChange(data);
                }
                lastValues.current = data;
            }
        },
        [onChange],
    );

    const callback = useDebounceCallback(deduplicateOnChange, debounceDelay);

    useEffect(() => {
        return subscribe({
            callback: ({ isValid, values }) => {
                if (allowInvalid || isValid) {
                    callback(values);
                }
            },
            formState: { isValid: !allowInvalid, values: true },
        });
    }, [allowInvalid, callback, subscribe]);
}
